import { MetadataItem } from '@/components/MetadataItem';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { UserChip } from '@/components/UserChip';
import { formatDate, formatDateTime } from '@/util/dates';
import type { Recall, Timestamp } from '@rock/types';
import type { FC } from 'react';
import { Link } from 'react-router-dom';
import { CodeTag } from './recallTags';

/** When each thing happened to this recall, and under which version. */
export const RecallHistoryTab: FC<{ recall: Recall }> = ({ recall }) => (
    <Card>
        <CardHeader>
            <CardTitle>History</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetadataItem label="Published at source" value={formatDate(recall.publishedAt)} />
            <MetadataItem label="Last published at source" value={formatDate(recall.lastPublishedAt)} />
            <MetadataItem label="Ingested" value={formatDateTime(recall.createdAt)} />
            <MetadataItem
                label="Classified"
                value={
                    recall.classifiedAt ? (
                        <VersionedAt at={recall.classifiedAt} version={recall.classifyVersion} />
                    ) : (
                        '—'
                    )
                }
            />
            <MetadataItem
                label="Processed"
                value={recall.processedAt ? <VersionedAt at={recall.processedAt} version={recall.rulesVersion} /> : '—'}
            />
            <MetadataItem
                label="Reviewed"
                value={
                    recall.reviewedAt ? (
                        <span className="flex items-center gap-2">
                            {formatDateTime(recall.reviewedAt)}{' '}
                            {recall.reviewedBy && <UserChip userId={recall.reviewedBy} />}
                        </span>
                    ) : (
                        '—'
                    )
                }
            />
            <MetadataItem label="Approved" value={recall.approvedAt ? formatDateTime(recall.approvedAt) : '—'} />
            <MetadataItem
                label="Rejected"
                value={
                    recall.rejectReason
                        ? `${recall.rejectReason}${recall.rejectNote ? ` · ${recall.rejectNote}` : ''}`
                        : '—'
                }
            />
            <MetadataItem
                label="Supersedes"
                value={
                    recall.supersedesRecallId ? (
                        <Link
                            className="text-primary underline-offset-2 hover:underline"
                            to={`/products/recalls/${encodeURIComponent(recall.supersedesRecallId)}`}
                        >
                            {recall.supersedesRecallId}
                        </Link>
                    ) : (
                        '—'
                    )
                }
            />
            <MetadataItem label="Last updated" value={formatDateTime(recall.updatedAt)} />
            <MetadataItem
                label="Notice hash"
                value={<span className="font-mono text-xs">{recall.noticeHash.slice(0, 16)}…</span>}
            />
            <MetadataItem
                label="Active brands"
                value={recall.activeBrandIds.length ? recall.activeBrandIds.length : 'none (not approved)'}
            />
        </CardContent>
    </Card>
);

const VersionedAt: FC<{ at: Timestamp; version: string | undefined }> = ({ at, version }) => (
    <span className="flex flex-wrap items-center gap-2">
        {formatDateTime(at)}
        {version && <CodeTag>{version}</CodeTag>}
    </span>
);
